import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { deletionRequest } from "@/lib/memberAccount";

const PATH = "/member/account/deletion-request";
const refusal = { 401: "Bitte melde dich erneut an.", 403: "Das Passwort oder der Kontostand wurde nicht bestätigt.",
  404: "Löschanfragen sind in diesem Core noch nicht verfügbar.", 409: "Diese Anfrage wurde inzwischen bearbeitet. Bitte lade den Stand neu.",
  422: "Bitte prüfe die Anfrage erneut.", 429: "Zu viele Versuche. Bitte warte 15 Minuten." };

// Only an explicit click writes. The retry key contains no password and survives an unclear response.
export function useDeletionRequest() {
  const [state, setState] = useState({ phase: "idle", reason: "", request: null });
  const mounted = useRef(true), flight = useRef(false), key = useRef(null), accepted = useRef(null);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const publish = (next) => { if (mounted.current) setState(next); };
  const run = async (mode, password) => {
    if (flight.current) return;
    if (mode === "send" && !key.current) {
      if (typeof globalThis.crypto?.randomUUID !== "function") {
        publish({ phase: "rejected", reason: "Das Senden ist in diesem Browser gerade nicht möglich.", request: null }); return;
      }
      key.current = globalThis.crypto.randomUUID();
    }
    flight.current = true;
    publish({ phase: "busy", reason: "", request: null });
    try {
      if (mode === "send") {
        try {
          const result = await api.post(PATH, { password }, { headers: { "Idempotency-Key": key.current } });
          if (!mounted.current) return;
          const parsed = deletionRequest(result.data);
          accepted.current = result.status === 202 && parsed?.request ? parsed.request.id : null;
        } catch (error) {
          if (!mounted.current) return;
          if (error.memberGuard || Object.prototype.hasOwnProperty.call(refusal, error.response?.status)) {
            publish({ phase: "rejected", reason: error.memberGuard ? "Das Senden ist in dieser Umgebung gesperrt." : refusal[error.response.status], request: null }); return;
          }
        }
      }
      if (!mounted.current) return;
      const result = await api.get(PATH);
      if (!mounted.current) return;
      const parsed = deletionRequest(result.data);
      if (parsed?.request && (!accepted.current || accepted.current === parsed.request.id)) {
        publish({ phase: accepted.current ? "confirmed" : "found", reason: "", request: parsed.request });
      } else publish({ phase: "unclear", reason: "Eine offene Anfrage konnte noch nicht bestätigt werden. Bitte prüfe den Stand erneut.", request: null });
    } catch {
      publish({ phase: "unclear", reason: "Der Stand konnte nicht geladen werden. Die Anfrage wird nicht automatisch erneut gesendet.", request: null });
    } finally { flight.current = false; }
  };
  return { ...state, busy: state.phase === "busy", send: (password) => run("send", password), check: () => run("check") };
}
